import { FF_RESULT_RESURRECTION_DIAGNOSTICS } from '@/consts/featureFlags';
import { applyFeatureFlagOverride } from '@/hooks/useFeatureFlag';
import { posthog } from '@/providers/posthog';
import { log } from '@/logger/logger';
import { useWorkflowStore } from '@/state/workflow.state';
import type { UploadedAsset } from '@/types/api/assets';

const logger = log.getLogger('WorkflowResults');
const MAX_LOGGED_RESULTS = 50;

export interface ResultDeliveryContext {
  source: 'socket' | 'poll' | 'catch_up' | 'execute' | 'cancel' | 'cancel_refresh' | 'cached_registration' | 'unknown';
  runningWorkflowRunIds?: string[];
  runningWorkflowRunCount?: number;
  handlerInstanceId?: string;
  nodeRunId?: string;
  workflowRunId?: string;
}

/** Records identifiers and run slots only, with a bounded payload even when a node is cleared in bulk. */
export function describeResults(assets: UploadedAsset[]) {
  return {
    resultCount: assets.length,
    resultsTruncated: assets.length > MAX_LOGGED_RESULTS,
    results: assets.slice(0, MAX_LOGGED_RESULTS).map((asset) => ({
      assetId: 'id' in asset ? asset.id : undefined,
      batchId: asset.batchId,
      order: asset.order,
      secondaryOrder: asset.secondaryOrder,
    })),
  };
}

/** Strictly opt-in, including the existing per-tab URL overrides; flag checks emit no exposure events. */
export function isResultDiagnosticsEnabled(): boolean {
  return (
    applyFeatureFlagOverride(
      FF_RESULT_RESURRECTION_DIAGNOSTICS,
      posthog?.isFeatureEnabled(FF_RESULT_RESURRECTION_DIAGNOSTICS, { send_event: false }),
      typeof window === 'undefined' ? '' : window.location.search,
    ) === true
  );
}

/** Defers payload construction until diagnostics are enabled. */
export function logResultDiagnostic(
  event:
    | 'workflow_result_deleted'
    | 'workflow_result_handler_lifecycle'
    | 'workflow_result_delivery'
    | 'workflow_result_append_decision',
  fields: () => Record<string, unknown>,
) {
  if (!isResultDiagnosticsEnabled()) return;
  logger.info(event, { workflowId: useWorkflowStore.getState().recipe?.id, ...fields() });
}
